import _ from 'lodash';
import { getNonEmptyValues } from '@/utils/search';
import { valueAsArray } from '@/utils/collections-util';

export const CLAIM_OPTIONS_MINE = {
  notSelected: { label: 'Not selected', value: '' },
  claimed: { label: 'RO\'s that I have claimed', value: 'claimed' },
  notClaimed: { label: 'RO\'s that I have <em>not</em> claimed', value: 'not_claimed' },
};

export const CLAIM_OPTIONS_OTHERS = {
  notSelected: { label: 'Not selected', value: '' },
  claimed: { label: 'RO\'s others have claimed', value: 'claimed' },
  notClaimed: { label: 'RO\'s others have <em>not</em> claimed', value: 'not_claimed' },
};

export const CLAIM_OPTIONS_RECENT = {
  notSelected: { label: 'Not selected', value: '' },
  claimedByMe: { label: 'RO\'s <em>I</em> have most recently claimed', value: 'claim_by_me' },
  claimedByOthers: { label: 'RO\'s <em>others</em> have most recently claimed', value: 'claim_by_others' },
};

class ResearchOutputSearchParam {
  constructor(eraId) {
    this.radarId = null;
    this.authorNameContains = '';
    this.eraId = eraId;
    this.excludeActive = false;
    this.researchOutputTypes = [];
    this.title = '';
    this.yearStart = null;
    this.yearEnd = null;
    this.publisherName = '';
    this.parentDoc = '';
    this.withMdJournalForc = false;
    this.journalForcIds = [];
    this.interestsForAllMyForcs = false;
    this.interestForcId = null;
    this.isIndigenousResearch = '';
    this.hasBylines = '';
    this.roStatus = null;
    this.withNoClaimForcApportions = false;
    this.claimForcId = null; // TO DEPRECATE: Cater for legacy-single-forc search component - e.g. PortfolioSearch.vue
    this.claimForcIds = []; // Cater for multi-forc search components - e.g. ResearchOutputSearch.vue
    this.facultyOrDepartmentName = null;
    this.withPeerReview = false;
    this.withPeerReviewNoResearchStatement = false;
    this.workQueue = '';
    this.pureId = '';
    this.portfolioId = null;
    this.hasPortfolioMembership = false;
    this.portfolioName = '';
    this.portfolioStatus = null;
    this.withNoPortfolioForcApportions = false;
    this.portfolioForcId = null;
    this.withPortfolioPeerReview = false;
    this.withPortfolioPeerReviewNoResearchStatement = false;
    this.eligibility = 'eligible';
    this.myClaims = CLAIM_OPTIONS_MINE.notSelected.value;
    this.otherUserClaims = CLAIM_OPTIONS_OTHERS.notSelected.value;
    this.recentlyClaimed = CLAIM_OPTIONS_RECENT.notSelected.value;
    this.claimsHasMyForcs = false;
    this.notClaimedToMyForcs = false;
    this.citationData = '';
    this.page = 1;
  }

  static create(params = {}) {
    const updateParams = {
      journalForcIds: valueAsArray(params.journalForcIds).map(Number),
      interestForcId: _.toInteger(params.interestForcId) || null,
      interestsForAllMyForcs: _.trim(params.interestsForAllMyForcs) === 'true',
      isIndigenousResearch: _.trim(params.isIndigenousResearch),
      hasBylines: _.trim(params.hasBylines),
      claimForcId: _.toInteger(params.claimForcId) || null,
      claimForcIds: valueAsArray(params.claimForcIds).map(Number),
      yearStart: _.toInteger(params.yearStart) || null,
      yearEnd: _.toInteger(params.yearEnd) || null,
      excludeActive: _.trim(params.excludeActive) === 'true',
      withMdJournalForc: _.trim(params.withMdJournalForc) === 'true',
      withNoClaimForcApportions: _.trim(params.withNoClaimForcApportions) === 'true',
      withPeerReview: _.trim(params.withPeerReview) === 'true',
      withPeerReviewNoResearchStatement: _.trim(params.withPeerReviewNoResearchStatement) === 'true',
      hasPortfolioMembership: _.trim(params.hasPortfolioMembership) === 'true',
      withNoPortfolioForcApportions: _.trim(params.withNoPortfolioForcApportions) === 'true',
      withPortfolioPeerReview: _.trim(params.withPortfolioPeerReview) === 'true',
      withPortfolioPeerReviewNoResearchStatement: _.trim(params.withPortfolioPeerReviewNoResearchStatement) === 'true',
      notClaimedToMyForcs: _.trim(params.notClaimedToMyForcs) === 'true',
      citationData: _.trim(params.citationData),
    };

    return Object.assign(
      new ResearchOutputSearchParam(),
      params,
      updateParams,
    );
  }

  toApiParams() {
    return getNonEmptyValues({
      radar_id: this.radarId,
      author_name_contains: this.authorNameContains,
      era_id: this.eraId,
      exclude_active: this.excludeActive,
      research_output_type: this.researchOutputTypes,
      research_output_title: this.title,
      pub_year_min: this.yearStart,
      pub_year_max: this.yearEnd,
      pub_name_contains: this.publisherName,
      parent_doc_contains: this.parentDoc,

      with_md_journal_forc: this.withMdJournalForc,
      journal_forc_ids: this.withMdJournalForc
        ? []
        : this.journalForcIds,

      interest_for_all_my_forcs: this.interestsForAllMyForcs,
      interest_forc_id: this.interestsForAllMyForcs
        ? null
        : this.interestForcId,

      is_indigenous_research: this.isIndigenousResearch,
      has_bylines: this.hasBylines,
      status: this.roStatus,
      faculty_department_name: this.facultyOrDepartmentName,
      with_no_claim_forc_apportions: this.withNoClaimForcApportions,

      claim_forc_id: this.withNoClaimForcApportions
        ? null
        : this.claimForcId,

      claim_forc_ids: this.withNoClaimForcApportions
        ? null
        : this.claimForcIds,

      with_peer_review: this.withNoClaimForcApportions
        ? null
        : this.withPeerReview,

      with_peer_review_no_research_statement: this.withNoClaimForcApportions
        ? null
        : this.withPeerReviewNoResearchStatement,

      work_queue: this.workQueue,
      pure_id: this.pureId,
      portfolio_id: this.portfolioId,
      has_portfolio_membership: this.hasPortfolioMembership,
      portfolio_name: this.portfolioName,
      portfolio_status: this.portfolioStatus,
      with_no_portfolio_forc_apportions: this.withNoPortfolioForcApportions,

      portfolio_forc_id: this.withNoPortfolioForcApportions
        ? null
        : this.portfolioForcId,

      with_portfolio_peer_review: this.withNoPortfolioForcApportions
        ? null
        : this.withPortfolioPeerReview,

      with_portfolio_peer_review_no_research_statement: this.withNoPortfolioForcApportions
        ? null
        : this.withPortfolioPeerReviewNoResearchStatement,

      filter_by_eligibility_status: this.eligibility,
      my_claims: this.myClaims,
      other_user_claims: this.otherUserClaims,
      recently_claimed: this.recentlyClaimed,
      claims_for_all_my_forcs: this.claimsHasMyForcs,
      not_claimed_to_my_forcs: this.notClaimedToMyForcs,
      filter_by_citation_data: this.citationData,
      page: this.page || 1,
    });
  }
}

export default ResearchOutputSearchParam;
